import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import MapView, { PROVIDER_GOOGLE } from "react-native-maps";
import { useFocusEffect } from "@react-navigation/native";
import Text from "@components/CustomText";
import { getLiveDbLocations } from "@services/api";
import Header from "../PartnerRoutingAndPacking/components/Header";
import { tokens, sharedStyles } from "../PartnerRoutingAndPacking/styles";
import DbMarker from "./components/DbMarker";
import HubMarker from "./components/HubMarker";
import DbListItem from "./components/DbListItem";
import ZoomControls from "./components/ZoomControls";
import { formatUpdatedAt } from "./utils";

const INDIGO = "#4F46E5";
const WHITE = "#FFFFFF";
const SHADOW = "#0F172A";

// Radius around the hub the initial viewport must show — ~5 km reads as
// "the spoke's neighbourhood" (sector-level detail with the fleet around
// the hub); 15 km was too far out.
const HUB_VIEW_RADIUS_KM = 5;

// Fallback viewport when neither the hub nor any DB has coordinates yet.
const INDIA_REGION = {
  latitude: 26.8,
  longitude: 78.5,
  latitudeDelta: 12,
  longitudeDelta: 12,
};

const LiveDbLocations = ({ navigation }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mapRef = useRef(null);
  const hasFittedRef = useRef(false);

  // No polling by design — data loads on focus and via the refresh button.
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getLiveDbLocations();
      setData(res?.data || null);
    } catch (_e) {
      setError("Could not fetch live locations");
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData]),
  );

  const dbs = useMemo(() => data?.dbs || [], [data]);
  const hub = useMemo(() => data?.hub || null, [data]);

  const markerDbs = useMemo(
    () => dbs.filter((d) => d.lat !== null && d.lng !== null),
    [dbs],
  );

  // OFD DBs first (server order — freshest ping — preserved within each
  // group); non-OFD rows render greyed out below them.
  const listDbs = useMemo(
    () => [...dbs].sort((a, b) => (b.is_ofd ? 1 : 0) - (a.is_ofd ? 1 : 0)),
    [dbs],
  );

  // Center the map on the hub once per screen visit (fall back to fitting
  // the DB pins when the CP has no cmo_spokes row); later refreshes keep
  // whatever viewport the user has panned to.
  const fitOnce = useCallback(() => {
    if (hasFittedRef.current || !mapRef.current) return;
    if (hub) {
      hasFittedRef.current = true;
      // Zoom so ~HUB_VIEW_RADIUS_KM around the hub is visible in every
      // direction (1° latitude ≈ 111 km; longitude shrinks by cos(lat)).
      const latDelta = (2 * HUB_VIEW_RADIUS_KM) / 111;
      const lngDelta = latDelta / Math.cos((hub.lat * Math.PI) / 180 || 1e-6);
      mapRef.current.animateToRegion(
        {
          latitude: hub.lat,
          longitude: hub.lng,
          latitudeDelta: latDelta,
          longitudeDelta: lngDelta,
        },
        300,
      );
      return;
    }
    const points = markerDbs.map((d) => ({
      latitude: d.lat,
      longitude: d.lng,
    }));
    if (!points.length) return;
    hasFittedRef.current = true;
    if (points.length === 1) {
      mapRef.current.animateToRegion(
        { ...points[0], latitudeDelta: 0.02, longitudeDelta: 0.02 },
        300,
      );
      return;
    }
    mapRef.current.fitToCoordinates(points, {
      edgePadding: { top: 80, right: 60, bottom: 80, left: 60 },
      animated: false,
    });
  }, [markerDbs, hub]);

  // onMapReady fires before the fetch returns, so re-run the one-shot
  // centering when the data actually lands.
  useEffect(() => {
    fitOnce();
  }, [fitOnce]);

  const focusDb = (db) => {
    if (db.lat === null || db.lng === null || !mapRef.current) return;
    mapRef.current.animateToRegion(
      {
        latitude: db.lat,
        longitude: db.lng,
        latitudeDelta: 0.01,
        longitudeDelta: 0.01,
      },
      300,
    );
  };

  const subtitle = data
    ? `${dbs.length} delivery boys · ${formatUpdatedAt(data.fetched_at)}`
    : "";

  return (
    <View style={sharedStyles.screen}>
      <Header
        title="Live Tracking"
        subtitle={subtitle}
        onBack={() => navigation.goBack()}
        right={
          <TouchableOpacity
            onPress={fetchData}
            disabled={loading}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            style={styles.refreshBtn}
            testID="live-db-refresh"
          >
            {loading ? (
              <ActivityIndicator size="small" color={tokens.textPrimary} />
            ) : (
              <Text style={styles.refreshGlyph}>↻</Text>
            )}
          </TouchableOpacity>
        }
      />

      <View style={styles.mapWrap}>
        <MapView
          ref={mapRef}
          provider={PROVIDER_GOOGLE}
          googleRenderer={"LEGACY"}
          style={StyleSheet.absoluteFill}
          initialRegion={INDIA_REGION}
          minZoomLevel={2}
          maxZoomLevel={20}
          toolbarEnabled={false}
          showsCompass={false}
          zoomControlEnabled={false}
          onMapReady={fitOnce}
          onLayout={fitOnce}
        >
          {hub ? <HubMarker hub={hub} /> : null}
          {markerDbs.map((db) => (
            <DbMarker key={db.cp_db_id} db={db} onPress={() => focusDb(db)} />
          ))}
        </MapView>
        <ZoomControls mapRef={mapRef} style={styles.zoomControls} />
      </View>

      <View style={styles.sheet}>
        <View style={styles.handle} />
        {error ? (
          <View style={sharedStyles.centered}>
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity style={styles.retryBtn} onPress={fetchData}>
              <Text style={styles.retryText}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <FlatList
            data={listDbs}
            keyExtractor={(item) => String(item.cp_db_id)}
            renderItem={({ item }) => (
              <DbListItem
                db={item}
                // Trail only for DBs marked OFD today; others just center
                // the map on their position.
                onPress={() =>
                  item.is_ofd
                    ? navigation.navigate("LiveDbTrail", { db: item })
                    : focusDb(item)
                }
              />
            )}
            ListEmptyComponent={
              loading ? null : (
                <Text style={sharedStyles.emptyText}>
                  No active delivery boys
                </Text>
              )
            }
          />
        )}
      </View>
    </View>
  );
};

export default LiveDbLocations;

const styles = StyleSheet.create({
  mapWrap: { flex: 1 },
  zoomControls: { bottom: 32 },
  refreshBtn: {
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
  },
  refreshGlyph: {
    color: tokens.textPrimary,
    fontSize: 22,
    fontWeight: "600",
  },
  sheet: {
    height: "42%",
    backgroundColor: tokens.surface,
    borderTopLeftRadius: tokens.radius.xl,
    borderTopRightRadius: tokens.radius.xl,
    marginTop: -tokens.radius.xl,
    paddingTop: tokens.spacing.small,
    elevation: 8,
    shadowColor: SHADOW,
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
  },
  handle: {
    alignSelf: "center",
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: tokens.borderMedium,
    marginBottom: tokens.spacing.small,
  },
  errorText: {
    color: tokens.textSecondary,
    marginBottom: tokens.spacing.medium,
  },
  retryBtn: {
    paddingHorizontal: tokens.spacing.xlarge,
    paddingVertical: tokens.spacing.small,
    borderRadius: tokens.radius.full,
    backgroundColor: INDIGO,
  },
  retryText: { color: WHITE, fontWeight: "700" },
});
